import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Ruler, Sparkles, Save } from 'lucide-react';
import { cn } from '@/lib/utils';
import { API } from '@/lib/api';
import {
  indiceCinturaCadera,
  complexion,
  pesoIdeal,
  interpretarGrasaCorporal,
  interpretarMasaMuscular,
} from '@/lib/antropometria';

const BADGE_BG = {
  'text-emerald-500': 'bg-emerald-100 dark:bg-emerald-900/30',
  'text-emerald-600': 'bg-emerald-100 dark:bg-emerald-900/30',
  'text-amber-500':   'bg-amber-100 dark:bg-amber-900/30',
  'text-rose-600':    'bg-rose-100 dark:bg-rose-900/30',
  'text-cyan-500':    'bg-cyan-100 dark:bg-cyan-900/30',
  'text-cyan-600':    'bg-cyan-100 dark:bg-cyan-900/30',
};

const toBadge = (info) => info
  ? { label: info.label, color: info.color, bg: BADGE_BG[info.color] || 'bg-muted' }
  : null;

const num = (v) => v !== '' && v != null ? parseFloat(v) : null;

/** Deurenberg 1991: estimación de % grasa a partir de IMC, edad y sexo. */
function estimarGrasaDeurenberg(peso, estatura, edad, sexo) {
  if (!peso || !estatura || !edad || !sexo) return null;
  const imc = parseFloat(peso) / Math.pow(parseFloat(estatura) / 100, 2);
  const pct = sexo === 'masculino'
    ? 1.20 * imc + 0.23 * edad - 16.2
    : 1.20 * imc + 0.23 * edad - 5.4;
  return Math.max(0, parseFloat(pct.toFixed(1)));
}

const Box = ({ label, value, unit, sub, subColor, badge }) => (
  <div className="rounded-lg border border-border/60 p-3 space-y-0.5">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-2xl font-bold">
      {value ?? '—'}
      {value && unit && <span className="text-sm font-normal text-muted-foreground ml-1">{unit}</span>}
    </p>
    {badge && (
      <span className={cn('inline-block text-xs font-semibold px-2 py-0.5 rounded-full', badge.bg, badge.color)}>
        {badge.label}
      </span>
    )}
    {sub && !badge && <p className={cn('text-xs font-medium', subColor || 'text-muted-foreground')}>{sub}</p>}
  </div>
);

export const AntropometriaCard = ({ patient, sexo, edad }) => {
  const [peso,    setPeso]    = useState(patient.peso ?? '');
  const [campos,  setCampos]  = useState({ cintura: '', cadera: '', muneca: '' });
  const [grasa,   setGrasa]   = useState('');
  const [musculo, setMusculo] = useState('');
  const [grasaOrigen, setGrasaOrigen] = useState('vacio');
  const [guardando, setGuardando]     = useState(false);

  const cargarUltimaMedicion = async () => {
    try {
      const res = await axios.get(`${API}/patients/${patient.id}/mediciones`);
      const ultima = res.data?.[res.data.length - 1];
      setCampos({
        cintura: ultima?.cintura ?? '',
        cadera:  ultima?.cadera  ?? '',
        muneca:  ultima?.muneca  ?? '',
      });
      setPeso(ultima?.peso ?? patient.peso ?? '');
      setMusculo(ultima?.masa_muscular ?? '');

      if (ultima?.grasa_corporal != null) {
        setGrasa(ultima.grasa_corporal);
        setGrasaOrigen('medicion');
      } else {
        const est = estimarGrasaDeurenberg(patient.peso, patient.estatura, edad, sexo);
        if (est !== null) { setGrasa(est); setGrasaOrigen('estimado'); }
      }
    } catch {
      const est = estimarGrasaDeurenberg(patient.peso, patient.estatura, edad, sexo);
      if (est !== null) { setGrasa(est); setGrasaOrigen('estimado'); }
    }
  };

  useEffect(() => {
    let activo = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/patients/${patient.id}/mediciones`);
        const ultima = res.data?.[res.data.length - 1];
        if (!activo) return;
        setCampos({
          cintura: ultima?.cintura ?? '',
          cadera:  ultima?.cadera  ?? '',
          muneca:  ultima?.muneca  ?? '',
        });
        setPeso(ultima?.peso ?? patient.peso ?? '');
        setMusculo(ultima?.masa_muscular ?? '');

        if (ultima?.grasa_corporal != null) {
          setGrasa(ultima.grasa_corporal);
          setGrasaOrigen('medicion');
        } else {
          const est = estimarGrasaDeurenberg(patient.peso, patient.estatura, edad, sexo);
          if (est !== null) { setGrasa(est); setGrasaOrigen('estimado'); }
        }
      } catch {
        const est = estimarGrasaDeurenberg(patient.peso, patient.estatura, edad, sexo);
        if (est !== null) { setGrasa(est); setGrasaOrigen('estimado'); }
      }
    })();
    return () => { activo = false; };
  }, [patient.id, patient.peso, patient.estatura, edad, sexo]);

  const handleGuardar = async () => {
    setGuardando(true);
    try {
      const payload = {
        paciente_id:    patient.id,
        fecha:          new Date().toISOString().split('T')[0],
        peso:           num(peso),
        cintura:        num(campos.cintura),
        cadera:         num(campos.cadera),
        muneca:         num(campos.muneca),
        grasa_corporal: num(grasa),
        masa_muscular:  num(musculo),
      };
      await axios.post(`${API}/patients/${patient.id}/mediciones`, payload);
      toast.success('Medición guardada');
      setGrasaOrigen('medicion');
      await cargarUltimaMedicion();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const icc       = useMemo(() => indiceCinturaCadera(campos.cintura, campos.cadera, sexo), [campos.cintura, campos.cadera, sexo]);
  const comp      = useMemo(() => complexion(patient.estatura, campos.muneca, sexo), [patient.estatura, campos.muneca, sexo]);
  const pi        = useMemo(() => pesoIdeal(patient.estatura, sexo), [patient.estatura, sexo]);
  const grasaInfo = useMemo(() => interpretarGrasaCorporal(grasa, sexo), [grasa, sexo]);
  const muscInfo  = useMemo(() => interpretarMasaMuscular(musculo, sexo, edad), [musculo, sexo, edad]);

  const set = (k, v) => setCampos(prev => ({ ...prev, [k]: v }));

  return (
    <Card className="bg-card border-border/50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Ruler className="h-4 w-4 text-emerald-500" />
            Antropometría
          </CardTitle>
          <button
            type="button"
            onClick={handleGuardar}
            disabled={guardando}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-medium px-3 py-1.5 transition-colors cursor-pointer"
          >
            <Save className="h-3.5 w-3.5" />
            {guardando ? 'Guardando…' : 'Guardar medición'}
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* Peso + Circunferencias */}
        <div className="grid grid-cols-4 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Peso (kg)</Label>
            <Input
              type="number" step="0.1" placeholder="70"
              value={peso}
              onChange={e => setPeso(e.target.value)}
            />
          </div>
          {[
            { k: 'cintura', label: 'Cintura (cm)', ph: '80' },
            { k: 'cadera',  label: 'Cadera (cm)',  ph: '95' },
            { k: 'muneca',  label: 'Muñeca (cm)',  ph: '16' },
          ].map(f => (
            <div key={f.k} className="space-y-1">
              <Label className="text-xs">{f.label}</Label>
              <Input
                type="number" step="0.1" placeholder={f.ph}
                value={campos[f.k]}
                onChange={e => set(f.k, e.target.value)}
              />
            </div>
          ))}
        </div>

        {/* Composición corporal */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1">
              % Grasa corporal
              {grasaOrigen === 'estimado' && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-sky-600 dark:text-sky-400">
                  <Sparkles className="h-3 w-3" />
                  estimado
                </span>
              )}
            </Label>
            <Input
              type="number" step="0.1" placeholder="22"
              value={grasa}
              onChange={e => { setGrasa(e.target.value); setGrasaOrigen('manual'); }}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">% Masa muscular</Label>
            <Input
              type="number" step="0.1" placeholder="35"
              value={musculo}
              onChange={e => setMusculo(e.target.value)}
            />
          </div>
        </div>

        {grasaOrigen === 'estimado' && (
          <p className="text-xs text-muted-foreground">
            <span className="font-medium text-sky-600 dark:text-sky-400">* % grasa estimado</span>
            {' '}con la fórmula de Deurenberg (IMC + edad + sexo). Edítalo si tienes el dato de bioimpedancia.
          </p>
        )}

        {/* ICC, complexión, peso ideal */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Box
            label="Índice cintura/cadera"
            value={icc?.valor}
            sub={icc
              ? (icc.riesgoAlto ? `Riesgo alto (≥ ${icc.umbral})` : `Normal (< ${icc.umbral})`)
              : 'Faltan cintura y cadera'}
            subColor={icc
              ? (icc.riesgoAlto ? 'text-rose-600' : 'text-emerald-600')
              : 'text-muted-foreground'}
          />
          <Box
            label="Complexión"
            value={comp?.valor}
            sub={comp ? comp.etiqueta : 'Faltan estatura y muñeca'}
            subColor={comp ? 'text-emerald-600' : 'text-muted-foreground'}
          />
          <Box
            label="Peso ideal"
            value={pi?.ideal}
            unit="kg"
            sub={pi ? `Máx ${pi.maximo} kg` : 'Falta estatura'}
          />
        </div>

        {/* % grasa y % músculo con badge de interpretación */}
        <div className="grid grid-cols-2 gap-3">
          <Box
            label={grasaOrigen === 'estimado' ? '% Grasa corporal (est.)' : '% Grasa corporal'}
            value={grasa || null}
            unit="%"
            badge={toBadge(grasaInfo)}
          />
          <Box
            label="% Masa muscular"
            value={musculo || null}
            unit="%"
            badge={toBadge(muscInfo)}
          />
        </div>

        {!sexo && (
          <p className="text-xs text-amber-600">
            Define el sexo del paciente para rangos correctos de ICC, complexión y composición corporal.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
